import {
  formatDetailAmount,
  formatPacePercent,
  paceStatusMessage,
  periodCopy,
} from "@/grid/lib/cell-detail-copy";
import { useIsMobile } from "@/grid/hooks/use-mobile";
import {
  DEFAULT_THRESHOLDS,
  getCellStatusForPeriod,
  resolveActual,
  type StatusColor,
  type StatusThresholds,
} from "@/grid/lib/pacing";
import type { MetricDefinition, PeriodDefinition } from "@/grid/core/types";
import { cn } from "@/lib/cn";
import { X } from "lucide-react";
import { useEffect } from "react";

interface GridCellDetailProps {
  open: boolean;
  onClose: () => void;
  teamName: string;
  metric: MetricDefinition;
  period: PeriodDefinition;
  actual: number | null;
  target: number | null;
  pacingTarget: number | null;
  thresholds?: StatusThresholds;
}

const statusRingClass: Record<StatusColor, string> = {
  green: "bg-[var(--color-status-green)]",
  yellow: "bg-[var(--color-status-yellow)]",
  red: "bg-[var(--color-status-red)]",
};

export function GridCellDetail({
  open,
  onClose,
  teamName,
  metric,
  period,
  actual,
  target,
  pacingTarget,
  thresholds = DEFAULT_THRESHOLDS,
}: GridCellDetailProps) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = periodCopy(period.id);
  const resolvedActual = resolveActual(actual);
  const status = getCellStatusForPeriod(period.id, actual, target, pacingTarget, thresholds);
  const pacePct = formatPacePercent(resolvedActual, pacingTarget);
  const statusMsg = paceStatusMessage(status);
  const hasGoal = target != null && target > 0;
  const hasPace = pacingTarget != null && pacingTarget > 0;

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col bg-[var(--color-background)] p-6 standalone-safe-top standalone-safe-bottom"
        role="dialog"
        aria-labelledby="grid-cell-detail-title"
      >
        <div className="flex flex-1 flex-col justify-center px-2">
          {pacePct != null ? (
            <div className="mb-8 flex flex-col items-center text-center">
              <div
                className={cn(
                  "flex h-28 w-28 items-center justify-center rounded-full text-3xl font-bold text-white",
                  statusRingClass[status],
                )}
                aria-label={`${pacePct}% of pace`}
              >
                {pacePct}%
              </div>
              <p className={cn("mt-4 text-lg font-semibold", statusMsg.className)}>
                {statusMsg.text}
              </p>
            </div>
          ) : (
            <p className={cn("mb-8 text-center text-lg font-semibold", statusMsg.className)}>
              {statusMsg.text}
            </p>
          )}

          <div className="mb-6 text-center">
            <h2
              id="grid-cell-detail-title"
              className="text-xl font-semibold text-[var(--color-foreground)]"
            >
              {teamName}
            </h2>
            <p className="mt-1 text-[var(--color-muted-foreground)]">
              {metric.label} · {copy.periodLabel}
            </p>
          </div>

          <div className="space-y-4 rounded-xl bg-[var(--color-muted)]/50 p-5">
            <div className="flex items-center justify-between">
              <span className="text-[var(--color-muted-foreground)]">Current</span>
              <span className="text-lg font-semibold text-[var(--color-foreground)]">
                {formatDetailAmount(resolvedActual, metric.format)}
              </span>
            </div>
            {hasGoal ? (
              <>
                <div className="h-px bg-[var(--color-border)]" />
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-muted-foreground)]">Target</span>
                  <span className="text-lg font-semibold text-[var(--color-foreground)]">
                    {formatDetailAmount(target, metric.format)}
                  </span>
                </div>
              </>
            ) : null}
            {hasPace ? (
              <>
                <div className="h-px bg-[var(--color-border)]" />
                <p className="text-center text-sm text-[var(--color-muted-foreground)]">
                  Based on where we are {copy.paceContext}, you should be at about{" "}
                  <span className="font-semibold text-[var(--color-foreground)]">
                    {formatDetailAmount(pacingTarget, metric.format)}
                  </span>{" "}
                  by now.
                </p>
              </>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-xl bg-[var(--color-muted)] py-4 text-base font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]/80"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="grid-cell-detail-title"
        className="relative w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex gap-5">
          <div className="min-w-0 flex-1 space-y-4 pr-2">
            <div>
              <h2
                id="grid-cell-detail-title"
                className="text-lg font-semibold text-[var(--color-foreground)]"
              >
                {teamName}
              </h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {metric.label} · {copy.periodLabel}
              </p>
            </div>

            <p className="text-sm text-[var(--color-foreground)]">
              You&apos;re at{" "}
              <span className="font-semibold">
                {formatDetailAmount(resolvedActual, metric.format)}
              </span>{" "}
              {copy.soFar}
            </p>

            {hasGoal ? (
              <p className="text-sm text-[var(--color-foreground)]">
                Your {copy.goalLabel} is{" "}
                <span className="font-semibold">{formatDetailAmount(target, metric.format)}</span>
              </p>
            ) : null}

            {hasPace ? (
              <p className="text-sm text-[var(--color-foreground)]">
                Based on where we are {copy.paceContext}, you should be at about{" "}
                <span className="font-semibold">
                  {formatDetailAmount(pacingTarget, metric.format)}
                </span>{" "}
                by now.
              </p>
            ) : null}

            {pacePct != null ? (
              <p className="text-sm text-[var(--color-foreground)]">
                You&apos;re at <span className="font-semibold">{pacePct}%</span> of where you need
                to be
              </p>
            ) : null}

            <p className={cn("text-sm font-medium", statusMsg.className)}>{statusMsg.text}</p>
          </div>

          {pacePct != null ? (
            <div
              className={cn(
                "flex h-20 w-20 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white",
                statusRingClass[status],
              )}
              aria-label={`${pacePct}% of pace`}
            >
              {pacePct}%
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
